<template>
  <div class="sidebar">
    <!-- Logo -->
    <div class="sidebar-logo">
      <img src="../../assets/logo.png" alt="logo" class="logo-img" />
      <span v-if="!collapsed" class="logo-title">会员中心</span>
    </div>

    <!-- 菜单：优先使用动态菜单（Vuex），fallback 到静态路由 -->
    <el-menu
      :default-active="activeMenu"
      :collapse="collapsed"
      :collapse-transition="false"
      background-color="#304156"
      text-color="#bfcbd9"
      active-text-color="#409EFF"
      router
    >
      <!-- 动态菜单（从后端加载） -->
      <template v-if="dynamicMenus.length > 0">
        <template v-for="menu in dynamicMenus">
          <!-- 一级目录：有多个叶子页面 → 折叠菜单（用 _group_+id 作为 index 避免与子项路径冲突） -->
          <el-submenu
            v-if="isDirectory(menu) && collectAllPages(menu).length > 1"
            :key="'dir_' + menu.id"
            :index="'_group_' + menu.id"
          >
            <template slot="title">
              <i v-if="menu.icon" :class="menu.icon"></i>
              <span>{{ menu.menuName }}</span>
            </template>
            <!-- 递归渲染子项（只显示 type=2 页面） -->
            <template v-for="child in menu.children">
              <el-submenu
                v-if="isDirectory(child) && getVisibleChildren(child).length > 0"
                :key="'sub_' + child.id"
                :index="'_group_' + child.id"
              >
                <template slot="title">
                  <i v-if="child.icon" :class="child.icon"></i>
                  <span>{{ child.menuName }}</span>
                </template>
                <el-menu-item
                  v-for="leaf in getVisibleChildren(child)"
                  :key="'leaf_' + leaf.id"
                  :index="resolvePath(leaf.path)"
                >
                  {{ leaf.menuName }}
                </el-menu-item>
              </el-submenu>
              <el-menu-item
                v-else-if="child.menuType === 2"
                :key="'item_' + child.id"
                :index="resolvePath(child.path)"
              >
                {{ child.menuName }}
              </el-menu-item>
            </template>
          </el-submenu>

          <!-- 一级目录：只有1个叶子页面 → 直接显示为菜单项（跳过中间目录层级） -->
          <el-menu-item
            v-else-if="isDirectory(menu) && collectAllPages(menu).length === 1"
            :key="'dir1_' + menu.id"
            :index="resolvePath(collectAllPages(menu)[0].path)"
          >
            <i v-if="collectAllPages(menu)[0].icon || menu.icon" :class="collectAllPages(menu)[0].icon || menu.icon"></i>
            <span slot="title">{{ collectAllPages(menu)[0].menuName }}</span>
          </el-menu-item>

          <!-- 顶级菜单（type=2 无 children）→ 直接显示为一级菜单项 -->
          <el-menu-item
            v-else-if="menu.menuType === 2 && menu.path"
            :key="'top_' + menu.id"
            :index="resolvePath(menu.path)"
          >
            <i v-if="menu.icon" :class="menu.icon"></i>
            <span slot="title">{{ menu.menuName }}</span>
          </el-menu-item>
        </template>
      </template>

      <!-- Fallback: 静态路由菜单（动态未加载时兜底，一般不会走到这里） -->
      <template v-else>
        <template v-for="route in staticRoutes">
          <el-menu-item
            v-if="!route.children || route.children.length === 0"
            :key="route.path"
            :index="route.path"
          >
            <i v-if="route.meta && route.meta.icon" :class="route.meta.icon"></i>
            <span slot="title">{{ route.meta ? route.meta.title : '' }}</span>
          </el-menu-item>

          <el-submenu
            v-else
            :key="route.path"
            :index="'_static_' + route.path"
          >
            <template slot="title">
              <i v-if="route.meta && route.meta.icon" :class="route.meta.icon"></i>
              <span>{{ route.meta ? route.meta.title : '' }}</span>
            </template>
            <el-menu-item
              v-for="child in route.children"
              :key="route.path + '/' + child.path"
              :index="route.path + (child.path === '' ? '' : '/') + child.path"
            >
              {{ child.meta ? child.meta.title : '' }}
            </el-menu-item>
          </el-submenu>
        </template>
      </template>
    </el-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'Sidebar',
  computed: {
    ...mapState('app', ['sidebarCollapsed']),
    ...mapState('user', ['menuList']),
    collapsed() {
      return this.sidebarCollapsed
    },
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },
    dynamicMenus() {
      return this.menuList || []
    },
    staticRoutes() {
      return this.$router.options.routes.filter(
        route => !route.hidden && route.children && route.children.length > 0
      )
    }
  },
  methods: {
    /**
     * 判断是否是目录类型（type=1 且有 children）
     */
    isDirectory(menu) {
      return menu.menuType === 1 && menu.children && menu.children.length > 0
    },

    /**
     * 获取可见的子页面列表（过滤 type=2）
     */
    getVisibleChildren(menu) {
      if (!menu.children) return []
      return menu.children.filter(c => c.menuType === 2)
    },

    /**
     * 解析路径：确保返回完整路径（/member, /order 等）
     */
    resolvePath(path) {
      if (!path) return ''
      // 后端存储的是 /member 或 member 格式，统一处理
      return path.startsWith('/') ? path : '/' + path
    },

    /**
     * 获取目录下第一个页面的路径（用于 el-submenu 默认激活）
     */
    getFirstPagePath(menu) {
      const pages = this.collectAllPages(menu)
      if (pages.length > 0) {
        return this.resolvePath(pages[0].path)
      }
      return ''
    },

    /**
     * 递归收集目录下所有 type=2 叶子节点
     */
    collectAllPages(node) {
      const result = []
      if (!node || !node.children) return result
      for (const child of node.children) {
        if (child.menuType === 2) {
          result.push(child)
        } else if (child.menuType === 1) {
          result.push(...this.collectAllPages(child))
        }
      }
      return result
    }
  }
}
</script>

<style lang="less" scoped>
.sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-logo {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #263445;
  padding: 0 12px;
  overflow: hidden;

  .logo-img {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }

  .logo-title {
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    margin-left: 10px;
    white-space: nowrap;
  }
}

.el-menu {
  border-right: none;
  flex: 1;
  overflow-y: auto;
}
</style>
